import { queuesForVariation } from '@axle-health/work-queue-definitions';
import { Ordering } from 'gql-types';

import { ContainerBulkAction } from '../ContainerVisitTable.types';
import {
    defaultEpisodeOfCareRowToLink,
    defaultEpisodeVisitsRowToLink,
    defaultVisitsRowToLink,
} from '../definitions';
import { definitionFor, fromDefinition } from '../fromDefinition';
import {
    EpisodeOfCareDashboardContainer,
    PatientDashboardContainer,
    VisitDashboardContainer,
} from '../types';
import { PENN_CONTAINER_KEYS } from './containerKeys/containerKeys';
import { IDashboardVariationConfig } from './types';

// Penn Medicine at Home WorkQueues.
// https://docs.google.com/document/d/12FP_gYozrrsNik_d_nfoerSW_JYSpjs9UfqjtOqKr6o/edit
//
// The service names these queues key on are declared in the definitions package, spelled
// as Penn's database spells them.

const PUBLISHED_QUEUES = queuesForVariation('PENN_MEDICINE');

const patientSchedulingLink = ({ id }: { id: string }): string => `/patients/${id}/scheduling`;
const allEpisodesSchedulingLink = ({ id }: { id: string }): string =>
    `/patients/${id}/scheduling?episode=all`;

const CANCEL_VISITS = { bulkActions: [ContainerBulkAction.CANCEL_VISITS] };

// A patient has an active order in a discipline with no active clinician on their care
// team. Sorting wants days since the assignment was needed, oldest first, which needs a
// derived backend field; full name is a placeholder.
const INCOMPLETE_CARE_TEAM_CONTAINER: PatientDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, PENN_CONTAINER_KEYS.incompleteCareTeam),
    {
        defaultEnabled: true,
        hoverText:
            "Patients who don't have a care team clinician for one of their ordered disciplines.",
        tableConfig: {
            rowToLink: patientSchedulingLink,
            emptyTableText: 'No patients with incomplete care teams.',
            ordering: { fullName: Ordering.ASC },
        },
    },
);

const SOC_CONTAINER: VisitDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, PENN_CONTAINER_KEYS.soc),
    {
        defaultEnabled: false,
        hoverText: 'A SOC visit exists but has not been scheduled.',
        tableConfig: {
            highlightLinkedVisit: true,
            rowToLink: defaultVisitsRowToLink,
            emptyTableText: 'No SOC visits to schedule.',
            ordering: { effectiveSortDate: Ordering.ASC },
        },
    },
);

const ROC_CONTAINER: VisitDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, PENN_CONTAINER_KEYS.roc),
    {
        defaultEnabled: false,
        hoverText: 'ROCs that need to be scheduled.',
        tableConfig: {
            highlightLinkedVisit: true,
            rowToLink: defaultVisitsRowToLink,
            emptyTableText: 'No ROC visits to schedule.',
            ordering: { effectiveSortDate: Ordering.ASC },
        },
    },
);

const EVAL_CONTAINER: VisitDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, PENN_CONTAINER_KEYS.eval),
    {
        defaultEnabled: false,
        hoverText: 'Evals that need to be scheduled.',
        tableConfig: {
            highlightLinkedVisit: true,
            rowToLink: defaultVisitsRowToLink,
            emptyTableText: 'No Eval visits to schedule.',
            ordering: { effectiveSortDate: Ordering.ASC },
        },
    },
);

const OVERDUE_VISITS_CONTAINER: VisitDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, PENN_CONTAINER_KEYS.overdueVisits),
    {
        defaultEnabled: true,
        hoverText: 'Upcoming visits more than 15 mins. overdue.',
        extraProps: { showClinicianColumn: true },
        tableConfig: {
            rowToLink: defaultVisitsRowToLink,
            emptyTableText: 'No visits overdue by more than 15 mins.',
            ordering: { effectiveSortDate: Ordering.ASC },
        },
    },
);

const POTENTIAL_RESCHEDULES_CONTAINER: VisitDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, PENN_CONTAINER_KEYS.potentialReschedules),
    {
        defaultEnabled: true,
        hoverText: 'Visits that need to be either rescheduled or marked as missed.',
        tableConfig: {
            rowToLink: defaultVisitsRowToLink,
            emptyTableText: 'No patient no-shows requiring attention.',
            ordering: { effectiveSortDate: Ordering.DESC },
        },
    },
);

const URGENT_VISITS_NEEDING_SCHEDULING_CONTAINER: VisitDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, PENN_CONTAINER_KEYS.urgentVisitsNeedingScheduling),
    {
        defaultEnabled: true,
        hoverText: 'Visits that need to be scheduled within the next 7 days.',
        tableConfig: {
            highlightLinkedVisit: true,
            rowToLink: defaultVisitsRowToLink,
            emptyTableText: 'No visits with expiring fulfillment windows.',
            ordering: { effectiveSortDate: Ordering.ASC },
        },
    },
);

// Sorting wants the soonest fulfillment window end date, which is a minimum across the
// episode's visits.
const PATIENTS_NEEDING_SCHEDULING_CONTAINER: EpisodeOfCareDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, PENN_CONTAINER_KEYS.patientsNeedingScheduling),
    {
        defaultEnabled: true,
        hoverText: 'Patients with visits that need to be scheduled.',
        tableConfig: {
            rowToLink: defaultEpisodeOfCareRowToLink,
            emptyTableText: 'No patients to schedule.',
            ordering: { patient: { fullName: Ordering.ASC } },
        },
    },
);

const TRANSFERRED_PATIENTS_WITH_PENDING_VISITS_CONTAINER: VisitDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, PENN_CONTAINER_KEYS.transferredPatientsWithPendingVisits),
    {
        defaultEnabled: false,
        hoverText: 'Transferred patients with scheduled visits that need to be cancelled.',
        extraProps: CANCEL_VISITS,
        tableConfig: {
            highlightLinkedVisit: true,
            rowToLink: defaultEpisodeVisitsRowToLink,
            emptyTableText: 'No transfers requiring attention.',
            ordering: { effectiveSortDate: Ordering.ASC },
        },
    },
);

const AGENCY_DISCHARGED_PATIENTS_WITH_PENDING_VISITS_CONTAINER: VisitDashboardContainer =
    fromDefinition(
        definitionFor(
            PUBLISHED_QUEUES,
            PENN_CONTAINER_KEYS.agencyDischargedPatientsWithPendingVisits,
        ),
        {
            defaultEnabled: false,
            hoverText:
                'Agency discharged patients with scheduled visits that need to be cancelled.',
            extraProps: CANCEL_VISITS,
            tableConfig: {
                highlightLinkedVisit: true,
                rowToLink: defaultEpisodeVisitsRowToLink,
                emptyTableText: 'No agency discharged patients with pending visits.',
                ordering: { effectiveSortDate: Ordering.ASC },
            },
        },
    );

const DISCIPLINE_DISCHARGE_PATIENTS_WITH_PENDING_VISITS_CONTAINER: VisitDashboardContainer =
    fromDefinition(
        definitionFor(
            PUBLISHED_QUEUES,
            PENN_CONTAINER_KEYS.disciplineDischargePatientsWithPendingVisits,
        ),
        {
            defaultEnabled: false,
            hoverText:
                'Discipline discharged patients with scheduled visits that need to be canceled.',
            extraProps: CANCEL_VISITS,
            tableConfig: {
                highlightLinkedVisit: true,
                rowToLink: defaultEpisodeVisitsRowToLink,
                emptyTableText: 'No pending visits after discipline discharge.',
                ordering: { effectiveSortDate: Ordering.ASC },
            },
        },
    );

const DISCONTINUED_FREQUENCIES_WITH_PENDING_VISITS_CONTAINER: VisitDashboardContainer =
    fromDefinition(
        definitionFor(
            PUBLISHED_QUEUES,
            PENN_CONTAINER_KEYS.discontinuedFrequenciesWithPendingVisits,
        ),
        {
            defaultEnabled: true,
            hoverText: 'Discontinued frequencies with scheduled visits that need to be cancelled.',
            extraProps: CANCEL_VISITS,
            tableConfig: {
                highlightLinkedVisit: true,
                rowToLink: defaultEpisodeVisitsRowToLink,
                emptyTableText: 'No visits tied to discontinued frequencies.',
                ordering: { effectiveSortDate: Ordering.ASC },
            },
        },
    );

const INACTIVE_CLINICIAN_VISITS_CONTAINER: VisitDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, PENN_CONTAINER_KEYS.inactiveClinicianVisits),
    {
        defaultEnabled: true,
        hoverText: 'Visits assigned to an inactive clinician.',
        tableConfig: {
            highlightLinkedVisit: true,
            rowToLink: defaultEpisodeVisitsRowToLink,
            emptyTableText: 'No visits assigned to inactive clinicians.',
            ordering: { effectiveSortDate: Ordering.ASC },
        },
    },
);

const PENDING_HHA_SUPERVISORY_CONTAINER: PatientDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, PENN_CONTAINER_KEYS.pendingHhaSupervisory),
    {
        defaultEnabled: false,
        hoverText: 'Patients needing an HHA Supervisory visit within the next 5 days.',
        tableConfig: {
            rowToLink: allEpisodesSchedulingLink,
            emptyTableText: 'No patients pending HHA supervisory visits.',
            ordering: { fullName: Ordering.ASC },
        },
    },
);

const PENDING_THERAPY_SUPERVISORY_CONTAINER: PatientDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, PENN_CONTAINER_KEYS.pendingTherapySupervisory),
    {
        defaultEnabled: false,
        hoverText: 'Patients needing a PT or OT Supervisory visit within the next 5 days.',
        tableConfig: {
            rowToLink: allEpisodesSchedulingLink,
            emptyTableText: 'No patients pending therapy supervisory visits.',
            ordering: { fullName: Ordering.ASC },
        },
    },
);

const PENDING_THERAPY_REEVALUATION_CONTAINER: PatientDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, PENN_CONTAINER_KEYS.pendingTherapyReevaluation),
    {
        defaultEnabled: false,
        hoverText: 'Patients needing a PT, OT, or ST re-eval visit within the next 5 days.',
        tableConfig: {
            rowToLink: patientSchedulingLink,
            emptyTableText: 'No patients pending re-evaluation.',
            ordering: { fullName: Ordering.ASC },
        },
    },
);

const UPCOMING_END_OF_EPISODE_CONTAINER: EpisodeOfCareDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, PENN_CONTAINER_KEYS.upcomingEndOfEpisode),
    {
        defaultEnabled: false,
        hoverText:
            'Patients nearing the end of their episode with no recert, discharge, or recent ROC visit scheduled.',
        tableConfig: {
            rowToLink: defaultEpisodeOfCareRowToLink,
            emptyTableText: 'No episodes pending end of care.',
            ordering: { patient: { fullName: Ordering.ASC } },
        },
    },
);

const VISITS_SCHEDULED_BEFORE_SOC_CONTAINER: VisitDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, PENN_CONTAINER_KEYS.visitsScheduledBeforeSoc),
    {
        defaultEnabled: false,
        hoverText: "Visits that are scheduled or completed prior to the patient's SOC.",
        tableConfig: {
            highlightLinkedVisit: true,
            rowToLink: defaultVisitsRowToLink,
            emptyTableText: 'No visits scheduled before SOCs.',
            ordering: { effectiveSortDate: Ordering.ASC },
        },
    },
);

const CONFLICTING_DISCHARGE_AND_RECERT_VISITS_CONTAINER: EpisodeOfCareDashboardContainer =
    fromDefinition(
        definitionFor(PUBLISHED_QUEUES, PENN_CONTAINER_KEYS.conflictingDischargeAndRecertVisits),
        {
            defaultEnabled: false,
            hoverText: 'Patients with more than 1 OASIS discharge or recert visit on an episode.',
            tableConfig: {
                rowToLink: defaultEpisodeOfCareRowToLink,
                emptyTableText: 'No episodes with multiple OASIS Discharge or Recert visits.',
                ordering: { patient: { fullName: Ordering.ASC } },
            },
        },
    );

const ACTIVE_PATIENTS_WITH_NO_VISITS_CONTAINER: PatientDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, PENN_CONTAINER_KEYS.activePatientsWithNoVisits),
    {
        defaultEnabled: true,
        hoverText: "Patients who are active but don't have any future visits scheduled.",
        tableConfig: {
            rowToLink: patientSchedulingLink,
            emptyTableText: 'No active patients without pending visits.',
            ordering: { fullName: Ordering.ASC },
        },
    },
);

const COMPLETED_VISITS_MISSING_AN_ORDER_CONTAINER: VisitDashboardContainer = fromDefinition(
    definitionFor(PUBLISHED_QUEUES, PENN_CONTAINER_KEYS.completedVisitsMissingAnOrder),
    {
        defaultEnabled: false,
        hoverText: "Completed SOCs/ROCs/Evals that aren't yet associated to an order.",
        tableConfig: {
            highlightLinkedVisit: true,
            rowToLink: defaultVisitsRowToLink,
            emptyTableText: 'No completed SOC/ROC/Eval visits unlinked to orders.',
            ordering: { effectiveSortDate: Ordering.ASC },
        },
    },
);

export const PENN_DASHBOARD_CONFIG: IDashboardVariationConfig = {
    containers: [
        /* #1  */ INCOMPLETE_CARE_TEAM_CONTAINER,
        /* #2  */ SOC_CONTAINER,
        /* #3  */ ROC_CONTAINER,
        /* #4  */ EVAL_CONTAINER,
        /* #5  */ OVERDUE_VISITS_CONTAINER,
        /* #6  */ POTENTIAL_RESCHEDULES_CONTAINER,
        /* #7  */ URGENT_VISITS_NEEDING_SCHEDULING_CONTAINER,
        /* #8  */ PATIENTS_NEEDING_SCHEDULING_CONTAINER,
        /* #9  */ TRANSFERRED_PATIENTS_WITH_PENDING_VISITS_CONTAINER,
        /* #10 */ AGENCY_DISCHARGED_PATIENTS_WITH_PENDING_VISITS_CONTAINER,
        /* #11 */ DISCIPLINE_DISCHARGE_PATIENTS_WITH_PENDING_VISITS_CONTAINER,
        /* #12 */ DISCONTINUED_FREQUENCIES_WITH_PENDING_VISITS_CONTAINER,
        /* #13 */ INACTIVE_CLINICIAN_VISITS_CONTAINER,
        /* #14 */ PENDING_HHA_SUPERVISORY_CONTAINER,
        /* #15 */ PENDING_THERAPY_SUPERVISORY_CONTAINER,
        /* #16 */ PENDING_THERAPY_REEVALUATION_CONTAINER,
        /* #17 */ UPCOMING_END_OF_EPISODE_CONTAINER,
        /* #18 */ VISITS_SCHEDULED_BEFORE_SOC_CONTAINER,
        /* #19 */ CONFLICTING_DISCHARGE_AND_RECERT_VISITS_CONTAINER,
        /* #20 */ ACTIVE_PATIENTS_WITH_NO_VISITS_CONTAINER,
        /* #21 */ COMPLETED_VISITS_MISSING_AN_ORDER_CONTAINER,
    ],
};
